import { http } from '@/libs/http';

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

export interface TokenTotals {
  issued: number;
  refreshed: number;
  failed: number;
  revoked: number;
}

export interface SeriesPoint {
  day: string;
  issued: number;
  refreshed: number;
  failed: number;
}

export interface ProviderInfo {
  provider: string;
  supports_refresh: boolean;
}

export interface Overview {
  days: number;
  totals: TokenTotals;
  series: SeriesPoint[];
  failure_reasons: { reason: string; count: number }[];
  providers: ProviderInfo[];
  live_sessions: number;
}

export type UsageGroup = 'tenant' | 'user' | 'provider' | 'application';

export interface UsageRow {
  key: string;
  issued: number;
  refreshed: number;
  failed: number;
  last_seen: string | null;
}

export interface TokenEvent {
  id: string;
  occurred_at: string;
  event_type: 'issue' | 'refresh' | 'revoke';
  outcome: 'success' | 'failure';
  reason: string | null;
  tenant: string;
  email: string | null;
  application: string | null;
  provider: string | null;
  session_id: string | null;
  ip_address: string | null;
}

export interface TokenSession {
  id: string;
  tenant: string;
  tenant_name: string | null;
  email: string;
  provider: string | null;
  status: 'active' | 'expired' | 'revoked';
  auth_time: string;
  last_activity_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  idp_refresh_supported: boolean;
}

export interface PolicyValues {
  access_token_minutes: number;
  idle_timeout_minutes: number;
  max_session_hours: number;
}

export interface DefaultPolicy extends PolicyValues {
  event_retention_days: number;
  source: 'saved' | 'environment';
  updated_by: string | null;
  updated_at: string | null;
}

export interface TenantPolicy extends PolicyValues {
  tenant_id: string;
  tenant_name: string | null;
  tenant_slug: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

export interface TokenSettings {
  default: DefaultPolicy;
  tenants: TenantPolicy[];
  limits: Record<keyof PolicyValues | 'event_retention_days', [number, number]>;
}

export interface TenantOption {
  id: string;
  name: string;
  slug: string;
  logo?: string;
}

const BASE = '/mgmt/v1/tokens';

const clean = (params: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  );

export const tokenApi = {
  overview: (days: number, tenant?: string) =>
    http.get<Overview>(`${BASE}/overview`, { params: clean({ days, tenant }) }),

  usage: (group_by: UsageGroup, days: number, tenant?: string) =>
    http.get<UsageRow[]>(`${BASE}/usage`, {
      params: clean({ group_by, days, tenant, limit: 8 }),
    }),

  events: (params: Record<string, unknown>) =>
    http.get<Paginated<TokenEvent>>(`${BASE}/events`, { params: clean(params) }),

  sessions: (params: Record<string, unknown>) =>
    http.get<Paginated<TokenSession>>(`${BASE}/sessions`, { params: clean(params) }),

  revokeSession: (id: string) => http.post(`${BASE}/sessions/${id}/revoke`),

  settings: () => http.get<TokenSettings>(`${BASE}/settings`),

  saveDefault: (body: PolicyValues & { event_retention_days: number }) =>
    http.put<TokenSettings>(`${BASE}/settings/default`, body),

  saveTenant: (tenantId: string, body: PolicyValues) =>
    http.put<TokenSettings>(`${BASE}/settings/tenants/${tenantId}`, body),

  deleteTenant: (tenantId: string) =>
    http.delete<TokenSettings>(`${BASE}/settings/tenants/${tenantId}`),

  tenants: async (): Promise<TenantOption[]> => {
    type Raw = { id: string; name: string; slug: string; logo_url?: string | null };
    const payload = await http.get<Raw[] | { items?: Raw[]; data?: Raw[] }>('/mgmt/v1/tenants');
    const list = Array.isArray(payload) ? payload : (payload.items ?? payload.data ?? []);
    return list.map((t) => ({ id: String(t.id), name: t.name, slug: t.slug, logo: t.logo_url || undefined }));
  },
};

export const errorMessage = (err: unknown, fallback: string) => {
  const e = err as { response?: { data?: { message?: string; detail?: string } }; message?: string };
  return e?.response?.data?.message ?? e?.response?.data?.detail ?? e?.message ?? fallback;
};
